import { envConfig } from '#constants/envConfig';
import { defaultLocale, locales } from '../constants/locales';

const STORAGE_KEY = 'locale';
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/**
 * Locale persistence for the native shell.
 *
 * On the web the choice is stored by the server (`utils/updateLocale` sets the cookie
 * via Set-Cookie) and read back synchronously on boot in `src/client.tsx`. The native
 * shell has no server, so it writes the same cookie itself — the WebView has a normal
 * cookie jar on the `https://localhost` app origin, which keeps the boot path unchanged.
 *
 * The cookie alone is not enough to rely on: Android only flushes the WebView cookie
 * store periodically, so a choice made just before the app is killed can be lost. The
 * value is therefore mirrored into Capacitor Preferences, which writes immediately, and
 * reconciled on the next cold start (see hooks/useNativeLocaleSync).
 *
 * Gated on IS_NATIVE so the @capacitor/preferences import is tree-shaken out of the web
 * bundle.
 */
export const setLocaleCookie = (locale: string) => {
  document.cookie = `${STORAGE_KEY}=${encodeURIComponent(locale)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}`;
};

export const readLocaleCookie = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)locale=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const setNativeLocale = async (locale: string) => {
  if (!envConfig.IS_NATIVE) {
    return;
  }

  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: STORAGE_KEY, value: locale });
  } catch {
    // Storage is best-effort: the cookie already carries the choice for this session.
  }
};

export const getNativeLocale = async (): Promise<string | null> => {
  if (!envConfig.IS_NATIVE) {
    return null;
  }

  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    return value ?? null;
  } catch {
    return null;
  }
};

const isSupported = (locale: string | null | undefined): locale is string =>
  Boolean(locale) && Object.keys(locales).includes(locale as string);

/**
 * Язык, с которым приложение стартует, – до того, как отрисуется первый экран.
 *
 * <p>Оболочка приложения отрисована заранее, и язык в ней запечён английский: гидрация активирует
 * его поверх всего, что знает кука. Маршрут `/{-$locale}` видит расхождение между адресом и
 * активным языком и перебрасывает на английский адрес. Итог на телефоне 14.09.2026: «Русский» в
 * настройках перезагружал приложение по-английски, кука `locale=ru` при этом стояла; язык
 * телефона при первом запуске не читался вовсе (отзыв 1★ в Play 13.09.2026 «нет русского»).
 *
 * <p>Порядок: кука (выбор в этом запуске) → Preferences (выбор, переживший смерть процесса) →
 * язык телефона → английский.
 */
export const resolveNativeBootLocale = async (cookieLocale: string | null): Promise<string> => {
  if (isSupported(cookieLocale)) {
    return cookieLocale;
  }
  const stored = await getNativeLocale();
  if (isSupported(stored)) {
    return stored;
  }
  const preferences = globalThis.navigator?.languages?.length
    ? globalThis.navigator.languages
    : [globalThis.navigator?.language ?? ''];
  for (const preference of preferences) {
    const language = preference.toLowerCase().split(/[-_]/)[0];
    if (isSupported(language)) return language;
  }
  return defaultLocale;
};
